import React, { useMemo, useState, useCallback, useRef } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { getAgentImageBaseFilter } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Tooltip from '@/components/Tooltip';
import { AGENT_ICON } from '@/constants/config';
import useFavoriteAgent from '@/hooks/useFavoriteAgent';
import useStartAgentWorkChat from '@/hooks/useStartAgentWorkChat';
import useStartAgentChat from '@/hooks/useStartAgentChat';
import AgentOwnerMenu from '@/components/Agents/AgentOwnerMenu';

const isWeb = Platform.OS === 'web';
const AVATAR = 64;
const RAIL_WIDTH = 196;

const stop = (event) => {
  event?.stopPropagation?.();
  event?.preventDefault?.();
};

function FavoriteStar({ agentId, colors }) {
  const { isSaved, toggleFavorite } = useFavoriteAgent(agentId);
  const { t } = useTranslation();
  return (
    <Tooltip label={t(isSaved ? 'pages.agents.removeFavoriteTooltip' : 'pages.agents.addFavoriteTooltip')}>
      <Pressable
        onPress={(event) => {
          stop(event);
          toggleFavorite();
        }}
        onPressIn={stop}
        accessibilityRole="button"
        accessibilityState={{ selected: isSaved }}
        accessibilityLabel={isSaved ? t('nav.routes.agentChat.unsave') : t('pages.agents.colFavorite')}
        style={({ hovered, pressed }) => [
          {
            width: 40,
            height: 40,
            borderRadius: 12,
            alignItems: 'center',
            justifyContent: 'center',
          },
          (hovered || pressed) && { backgroundColor: colors.solid_color_secondary },
        ]}
      >
        <Icon
          name="star"
          solid={isSaved}
          size={15}
          color={isSaved ? colors.accent_color_primary : colors.text_color_tertiary}
        />
      </Pressable>
    </Tooltip>
  );
}

function AgentCard({
  agent,
  onPress,
  onUseAgent,
  isLoading,
  width: explicitWidth,
  fillHeight = true,
  variant = 'default',
}) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const startAgentWorkChat = useStartAgentWorkChat();
  const startAgentChat = useStartAgentChat();
  const [hovered, setHovered] = useState(false);
  const isMenuOpenRef = useRef(false);
  const menuClosedAtRef = useRef(0);
  const isRail = variant === 'rail';
  const styles = useMemo(
    () => createStyles(colors, explicitWidth, fillHeight, isRail),
    [colors, explicitWidth, fillHeight, isRail],
  );
  const jobRoles = (agent?.jobRoles || []).filter(role => typeof role === 'string' && role.trim()).slice(0, 3);
  const imageFilter = getAgentImageBaseFilter(theme, !!agent?.flags?.isImageInvertable);
  const filteredStyle = agent?.flags?.isImageInvertable && imageFilter ? { filter: imageFilter } : null;

  const handleMenuStateChange = useCallback((open) => {
    isMenuOpenRef.current = open;
    if (!open) menuClosedAtRef.current = Date.now();
  }, []);

  const handleOpenChat = useCallback(() => {
    if (isMenuOpenRef.current || Date.now() - menuClosedAtRef.current < 350) return;
    if (onUseAgent) onUseAgent(agent);
    else if (onPress) onPress(agent);
    else startAgentWorkChat(agent, { source: 'agent_card_use_agent', requestTask: true });
  }, [onUseAgent, onPress, agent, startAgentWorkChat]);

  const handleQuickChat = useCallback((event) => {
    stop(event);
    if (isMenuOpenRef.current || Date.now() - menuClosedAtRef.current < 350) return;
    startAgentChat(agent);
  }, [agent, startAgentChat]);

  if (isLoading) {
    return (
      <View style={styles.card} aria-busy={true}>
        <View style={styles.headerIdentity}>
          <View style={[styles.skeleton, styles.avatar]} />
          <View style={[styles.skeleton, { height: 20, flex: 1 }]} />
        </View>
        <View style={[styles.skeleton, { height: 40, width: '95%' }]} />
        <View style={[styles.skeleton, { height: 36 }]} />
        <View style={[styles.skeleton, { height: 44, marginTop: 4 }]} />
      </View>
    );
  }
  if (!agent) return null;

  const categoryLabel = agent.category?.name || agent.category?.slug;
  const useBotLabel = t('pages.agents.putBotToWork');

  return (
    <View style={[styles.card, hovered && styles.cardHovered]}>
      <Pressable
        onPress={handleOpenChat}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        accessibilityRole="button"
        accessibilityLabel={agent.name}
        accessibilityHint={useBotLabel}
        style={[StyleSheet.absoluteFillObject, { borderRadius: 14 }]}
      />
      <View style={styles.topRow} pointerEvents="box-none">
        <View style={styles.headerIdentity} pointerEvents="none">
          <View style={[styles.avatar, isWeb && filteredStyle]}>
            {agent.avatarPath ? (
              <Image
                source={{ uri: agent.avatarPath }}
                style={[styles.avatarImage, !isWeb && filteredStyle]}
                contentFit="cover"
              />
            ) : (
              <Icon name={AGENT_ICON} size={36} color={colors.text_color_secondary} />
            )}
          </View>
          <View style={styles.heading}>
            <View style={styles.nameRow}>
              <Text weight="bold" color="primary" numberOfLines={2} style={styles.title}>
                {agent.name}
              </Text>
              {!!agent.flags?.isPlusOnly && (
                <View style={styles.plusPill}>
                  <Text size="xxs" weight="bold" color="secondary">PLUS</Text>
                </View>
              )}
            </View>
            {!!categoryLabel && (
              <Text size="xxs" color="tertiary" weight="semibold" numberOfLines={1}>
                {categoryLabel}
              </Text>
            )}
          </View>
        </View>
        {/* Actions sit above the full-card target and never launch a job. */}
        <View style={styles.actions}>
          {!agent.isOwner && <FavoriteStar agentId={agent.id} colors={colors} />}
          <AgentOwnerMenu agent={agent} size={40} iconSize={15} onMenuStateChange={handleMenuStateChange} />
        </View>
      </View>

      <View style={styles.identity} pointerEvents="none">
        {!!agent.description && (
          <Text size="s" color="secondary" numberOfLines={2} style={styles.description}>
            {agent.description}
          </Text>
        )}
        {jobRoles.length > 0 && (
          <View style={styles.jobRoles}>
            <Text size="xs" color="tertiary" numberOfLines={2} accessibilityLabel={`${t('pages.agents.keyJobRoles')}: ${jobRoles.join(', ')}`} style={styles.jobRolesText}>
              {jobRoles.join(' · ')}
            </Text>
          </View>
        )}
      </View>

      <Pressable
        onPress={handleQuickChat}
        accessibilityRole="button"
        accessibilityLabel={`${t('pages.agents.quickChatWithBot')} · ${agent.name}`}
        style={({ hovered: active, pressed }) => [styles.cta, (active || pressed) && styles.ctaHovered]}
      >
        <Text size="s" weight="semibold" color="primary">{t('pages.agents.quickChatWithBot')}</Text>
        <Icon name="arrow-right" size={12} color={colors.text_color_secondary} />
      </Pressable>

      <Pressable
        onPress={handleOpenChat}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        accessibilityRole="button"
        accessibilityLabel={`${useBotLabel} · ${agent.name}`}
        style={({ hovered: active, pressed }) => [styles.cta, (active || pressed) && styles.ctaHovered]}
      >
        <Text size="s" weight="semibold" color="primary">{useBotLabel}</Text>
        <Icon name="arrow-right" size={12} color={colors.text_color_secondary} />
      </Pressable>
    </View>
  );
}

const createStyles = (colors, explicitWidth, fillHeight, isRail) => StyleSheet.create({
  card: {
    width: explicitWidth != null ? explicitWidth : (isRail ? RAIL_WIDTH : '100%'),
    // Percentage heights expand against the native ScrollView's content,
    // pushing the action far below the card. Web grids have bounded rows.
    ...(isWeb && fillHeight && !isRail ? { alignSelf: 'stretch', height: '100%' } : {}),
    minWidth: 0,
    minHeight: isRail ? 250 : 224,
    padding: 14,
    gap: 12,
    borderWidth: 1,
    borderRadius: 14,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    ...(isWeb ? { transition: 'border-color 150ms ease, background-color 150ms ease' } : {}),
  },
  cardHovered: { borderColor: colors.border_color_primary },
  topRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 6,
  },
  headerIdentity: { flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 10 },
  heading: { flex: 1, minWidth: 0, gap: 3 },
  avatar: {
    width: AVATAR,
    height: AVATAR,
    borderRadius: 16,
    backgroundColor: colors.solid_color_tertiary,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  avatarImage: { width: AVATAR, height: AVATAR },
  actions: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  identity: { flex: 1, minWidth: 0, gap: 8 },
  nameRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  title: { fontSize: 16, lineHeight: 21, letterSpacing: -0.3, flexShrink: 1 },
  plusPill: {
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 3,
    backgroundColor: colors.solid_color_secondary,
  },
  description: { lineHeight: 20 },
  jobRoles: {
    paddingTop: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border_color_secondary,
  },
  jobRolesText: { lineHeight: 18 },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    minHeight: 44,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_secondary,
  },
  ctaHovered: { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_primary },
  skeleton: { backgroundColor: colors.solid_color_tertiary, borderRadius: 8 },
});

AgentCard.propTypes = {
  agent: PropTypes.object,
  onPress: PropTypes.func,
  onUseAgent: PropTypes.func,
  isLoading: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fillHeight: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'rail']),
};

export default React.memo(AgentCard);
